import { ALL_DATA_TYPES } from '@repo/advanced-filters';
import {
  BooleanInput, DateTimeInput, DropdownInput, type DropdownInputProps,
  OwnerSelectDropdown, PhoneNumberInput,
  RemoteSearchableDropdownInput,
} from '@repo/ui';
import { type DataTypeConfigType, InputTypeConfigType } from '@repo/utils';
import {
  type DatePickerProps,
  Flex,
  Input, InputNumber, type InputNumberProps, type InputProps,
  type RadioGroupProps,
  type SelectProps,
  type TimePickerProps,
} from 'antd';
import { type TextAreaProps } from 'antd/es/input';

import { LeadSourceSelect } from '../LeadSourceSelect';
import { LeadStageSelect } from '../LeadStageSelect';

export interface ReferenceInputProps {
  changeValue: 'id' | 'label';
}

export interface DataTypeSpecificInputProps {
  value: string | string[] | undefined;
  dataType: string;
  dataTypeConfig?: DataTypeConfigType;
  disabled?: boolean;
  onChange?: (value: string | string[]) => void;

  inputProps?: InputProps;
  inputAreaProps?: TextAreaProps;
  inputNumberProps?: InputNumberProps;
  dropdownInputProps?: DropdownInputProps & (
    | {
      remoteSearchable: Boolean;
      searchString: string;
      setSearchString: (value: string) => void;
    }
  );
  referenceInputProps?: ReferenceInputProps;
  booleanInputProps?: RadioGroupProps;
  ownerInputProps?: SelectProps;
  leadStageSourceProps?: DropdownInputProps;
  dateInputProps?: DatePickerProps;
  timeInputProps?: TimePickerProps;
}

export default function DataTypeSpecificInput({
  value,
  dataType,
  dataTypeConfig,
  disabled,
  onChange = () => {},

  inputProps,
  inputAreaProps,
  inputNumberProps,
  dropdownInputProps,
  referenceInputProps,
  booleanInputProps,
  ownerInputProps,
  leadStageSourceProps,
  dateInputProps,
  timeInputProps,
}: DataTypeSpecificInputProps) {
  const {
    remoteSearchable,
    setSearchString,
    searchString,
    ...restDropdownInputProps
  } = dropdownInputProps ?? {};

  switch (dataType) {
    case ALL_DATA_TYPES.number:
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Enter a Value"
          value={value as string}
          onChange={(val) => onChange(val?.toString() ?? '')}
          disabled={!!disabled}
          {...inputNumberProps}
        />
      );
    case ALL_DATA_TYPES.string:
      if (dataTypeConfig?.inputTypeConfig?.toLowerCase() === 'phone') {
        return (
          <PhoneNumberInput
            numberInputProps={{
              value,
              ...inputProps,
            }}
            onNumberChange={(countryCode, phoneNumber) => onChange(`+${countryCode}-${phoneNumber}`)}
          />
        );
      } else if (dataTypeConfig?.inputTypeConfig?.toLowerCase() === InputTypeConfigType.TEXT_AREA) {
        return (
          <Input.TextArea
            placeholder="Enter a Value"
            value={value}
            maxLength={dataTypeConfig?.maxTextLength ?? 1000}
            onChange={(e) => onChange(e.target.value)}
            showCount
            disabled={!!disabled}
            {...inputAreaProps}
          />
        );
      } else {
        return (
          <Input
            placeholder="Enter a Value"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={!!disabled}
            {...inputProps}
          />
        );
      }
    case ALL_DATA_TYPES.dropdown:
    case ALL_DATA_TYPES.default:
    case ALL_DATA_TYPES.multi_select:
      if (remoteSearchable) {
        return (
          <RemoteSearchableDropdownInput
            value={value}
            mode={dataType === 'multi_select' ? 'multiple' : undefined}
            onChange={onChange}
            disabled={!!disabled}
            searchString={searchString}
            setSearchString={setSearchString}
            {...restDropdownInputProps}
          />
        );
      } else {
        return (
          <DropdownInput
            value={value}
            mode={dataType === 'multi_select' ? 'multiple' : undefined}
            onChange={onChange}
            disabled={!!disabled}
            {...restDropdownInputProps}
          />
        );
      }
    case ALL_DATA_TYPES.boolean:
      return (
        <BooleanInput
          value={value}
          onChange={(val) => onChange(val.target.value)}
          disabled={!!disabled}
          {...booleanInputProps}
        />
      );
    case ALL_DATA_TYPES.datetime:
      return (
        <Flex gap={8}>
          <DateTimeInput
            initialValue={value as string}
            onChange={(val) => onChange(val.toISOString())}
            disabled={!!disabled}
            dateInputProps={dateInputProps}
            timeInputProps={timeInputProps}
            pastTimeNotAllowed={dataTypeConfig?.pastTimeNotAllowed ?? false}
          />
        </Flex>
      );
    case ALL_DATA_TYPES.date:
      return (
        <DateTimeInput
          withoutTime
          initialValue={value as string}
          onChange={(val) => onChange(val.toISOString())}
          disabled={!!disabled}
          dateInputProps={dateInputProps}
          pastTimeNotAllowed={dataTypeConfig?.pastTimeNotAllowed ?? false}
        />
      );
    case ALL_DATA_TYPES.reference_lead_stage:
      return (
        <LeadStageSelect
          {...referenceInputProps}
          onChange={(val) => onChange(val)}
          value={value as string}
          changeValue={referenceInputProps?.changeValue as 'id' | 'label'}
          disabled={!!disabled}
          {...leadStageSourceProps}
        />
      );
    case ALL_DATA_TYPES.reference_lead_source:
      return (
        <LeadSourceSelect
          onChange={(val) => onChange(val)}
          value={value as string}
          changeValue={referenceInputProps?.changeValue as 'id' | 'label'}
          disabled={!!disabled}
          {...leadStageSourceProps}
        />
      );
    case ALL_DATA_TYPES.reference_user:
      return (
        <OwnerSelectDropdown
          value={value}
          handleClick={(val) => onChange(val)}
          multiple={false}
          forceFetch
          disabled={!!disabled}
          selectProps={{ ...ownerInputProps }}
        />
      );
    default:
      return null;
  }
}
